// src/hooks/useFactorApi.tsx

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { SensorId } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

type FactorContextValue = {
  byId: Record<string, any[]>
  setById: Dispatch<SetStateAction<Record<string, any[]>>>
  refresh: (_opts?: { sensorId?: SensorId; force?: boolean }) => Promise<any>
  getBySensorId: (
    _sensorId: SensorId,
    _opts?: { force?: boolean }
  ) => Promise<any>
}

const FactorCtx = createContext<FactorContextValue>({
  byId: {},

  setById: () => {},

  refresh: async () => null,

  getBySensorId: async () => null,
})

export const FactorDataProvider = ({ children }: { children: ReactNode }) => {
  const { start, stop } = usePreloader()

  const [byIdState, setByIdState] = useState<Record<string, any[]>>({})
  const reqsRef = useRef({})

  const setById = useCallback(updater => {
    setByIdState(prev =>
      typeof updater === 'function' ? updater(prev) : updater || {}
    )
  }, [])

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          e2.status = e2?.status ?? e2?.response?.status ?? 401
          throw e2
        }
      }

      throw {
        message: error?.message || String(error),
        status,
      }
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const fetchBySensorId = useCallback(
    async (id: SensorId, { force = false } = {}) => {
      if (!id) return []
      const key = String(id)

      if (reqsRef.current[key] && !force) return reqsRef.current[key]

      reqsRef.current[key] = (async () => {
        await refreshToken()

        const url = `${REACT_APP_API_URL}/sensorfactor/all/${id}`
        const options = { method: 'GET', headers: { ...authHeader() } }

        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const response = await fetch(url, options)
              if (response.status === 204) return []
              if (!response.ok) {
                const text = await response.text()
                throw {
                  message: text || `HTTP ${response.status}`,
                  status: response.status,
                }
              }
              const payload = await response.json()
              return payload?.item?.data ?? payload?.data ?? []
            })
          )

          const list = Array.isArray(data) ? data : []
          setById(prev => ({ ...prev, [key]: list }))
          return list
        } finally {
          reqsRef.current[key] = null
        }
      })()

      return reqsRef.current[key]
    },
    [withRequest, withPreloader, setById]
  )

  const refresh = useCallback(
    ({
      sensorId,
      force = false,
    }: { sensorId?: SensorId; force?: boolean } = {}) =>
      fetchBySensorId(sensorId, { force }),
    [fetchBySensorId]
  )

  const getBySensorId = useCallback(
    (id: SensorId, opts) => fetchBySensorId(id, opts),
    [fetchBySensorId]
  )

  const ctxValue = useMemo(
    () => ({ byId: byIdState, setById, refresh, getBySensorId }),
    [byIdState, setById, refresh, getBySensorId]
  )

  return <FactorCtx.Provider value={ctxValue}>{children}</FactorCtx.Provider>
}

export const useFactor = (id: SensorId) => {
  const { byId, getBySensorId } = useContext(FactorCtx)
  const key = String(id || '')

  const cached =
    key && Array.isArray(byId[key]) && byId[key].length > 0 ? byId[key] : null

  const [data, setData] = useState([])
  const [isError, setError] = useState(null)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!id) return

      setLoading(true)
      setError(null)
      try {
        const result = cached ?? (await getBySensorId(id))
        if (!cancelled) setData(Array.isArray(result) ? result : [])
      } catch (error: any) {
        if (!cancelled) setError(error?.message || 'Fehler beim Laden')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [id, cached, getBySensorId])

  return { data, isError, isLoading }
}

export const useFactorApi = () => {
  const [isLoading, setLoading] = useState(false)
  const [isError, setError] = useState(null)
  const { setById } = useContext(FactorCtx)

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          e2.status = e2?.status ?? e2?.response?.status ?? 401
          throw e2
        }
      }

      throw {
        message: error?.message || String(error),
        status,
      }
    }
  }, [])

  const toNumOrNull = useCallback(v => {
    if (v === null) return null
    const s = String(v).trim()
    return s.length === 0 ? null : parseFloat(s)
  }, [])

  const recordData = useCallback(
    data => {
      const {
        entity_id,
        sensor_id,
        period_start,
        period_end,
        basic_charge_per_year,
        price_per_unit,
        pulse_value,
        caloric_value,
        conversion_factor,
        meter_conversion_factor,
        transformer_ratio,
        co2_factor,
        number_of_employees,
        area_in_sqm,
      } = data

      return {
        data: {
          id: entity_id,
          type: 'sensor_factor',
          attributes: {
            sensor_id: parseFloat(sensor_id),
            period_start,
            period_end,
            basic_charge_per_year: toNumOrNull(basic_charge_per_year),
            price_per_unit: toNumOrNull(price_per_unit),
            pulse_value: parseFloat(pulse_value),
            caloric_value: parseFloat(caloric_value),
            conversion_factor: parseFloat(conversion_factor),
            meter_conversion_factor: parseFloat(meter_conversion_factor),
            transformer_ratio: parseFloat(transformer_ratio),
            co2_factor: toNumOrNull(co2_factor),
            number_of_employees: toNumOrNull(number_of_employees),
            area_in_sqm: toNumOrNull(area_in_sqm),
          },
        },
      }
    },
    [toNumOrNull]
  )

  const getAll = useCallback(
    async (id: SensorId) => {
      setError(null)
      setLoading(true)

      try {
        return await withRequest(async () => {
          await refreshToken()

          const url = `${REACT_APP_API_URL}/sensorfactor/all/${id}`
          const options = { headers: { ...authHeader() }, method: 'GET' }
          const response = await fetch(url, options)

          if (response.status === 204) return []
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }

          const payload = await response.json()
          const list = payload?.item?.data ?? payload?.data ?? []

          setById(prev => ({ ...prev, [String(id)]: list }))
          return list
        })
      } catch (error) {
        const message = error?.message || 'Fehler beim Laden'
        setError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    [setById, withRequest]
  )

  const save = useCallback(
    async data => {
      setError(null)
      if (!data?.sensor_id) {
        const msg = LC.FACTOR_SENSOR_REQUIRED
        setError(msg)
        notifyError(msg)
        return null
      }

      setLoading(true)
      try {
        return await withRequest(async () => {
          await refreshToken()

          const url = `${REACT_APP_API_URL}/sensorfactor/`
          const options = {
            method: 'POST',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(recordData(data)),
          }
          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          const result = await response.json()
          notifySuccess(LC.FACTOR_SAVED)

          const key = String(data.sensor_id)
          const newItem = (result?.item?.data ?? result?.data ?? null) || data
          setById(prev => {
            const prevList = Array.isArray(prev[key]) ? prev[key] : []
            return { ...prev, [key]: [...prevList, newItem] }
          })

          return result?.id ?? null
        })
      } catch (error) {
        const message = error?.message || 'Fehler beim Speichern'
        setError(message)
        notifyError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    [recordData, setById, withRequest]
  )

  const update = useCallback(
    async (data, sensorFactorId) => {
      setError(null)
      if (!data?.sensor_id) {
        const msg = LC.FACTOR_SENSOR_REQUIRED
        setError(msg)
        notifyError(msg)
        return null
      }

      setLoading(true)
      try {
        return await withRequest(async () => {
          await refreshToken()

          const url = `${REACT_APP_API_URL}/sensorfactor/${sensorFactorId}`
          const options = {
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            method: 'PATCH',
            body: JSON.stringify(recordData(data)),
          }
          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          notifySuccess(LC.FACTOR_UPDATED)

          const key = String(data.sensor_id)
          setById(prev => {
            const list = Array.isArray(prev[key]) ? prev[key] : []
            const replaced = list.map(it =>
              it?.id === sensorFactorId || it?.entity_id === sensorFactorId
                ? { ...it, ...data }
                : it
            )
            return { ...prev, [key]: replaced }
          })

          return sensorFactorId
        })
      } catch (error) {
        const message = error?.message || 'Fehler beim Aktualisieren'
        setError(message)
        notifyError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    [recordData, setById, withRequest]
  )

  const deleteSensorFactor = useCallback(
    async (id, sensorId: SensorId) => {
      setError(null)
      if (!id) {
        const msg = LC.FACTOR_ID_REQUIRED
        setError(msg)
        notifyError(msg)
        return false
      }

      setLoading(true)
      try {
        return await withRequest(async () => {
          await refreshToken()

          const url = `${REACT_APP_API_URL}/sensorfactor/${id}`
          const options = {
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            method: 'DELETE',
          }
          const response = await fetch(url, options)
          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }

          notifySuccess(LC.FACTOR_DELETED)

          if (sensorId) {
            const key = String(sensorId)
            setById(prev => {
              const list = Array.isArray(prev[key]) ? prev[key] : []
              return {
                ...prev,
                [key]: list.filter(it => it?.id !== id && it?.entity_id !== id),
              }
            })
          }

          return true
        })
      } catch (error) {
        const message = error?.message || 'Fehler beim Löschen'
        setError(message)
        notifyError(message)
        return false
      } finally {
        setLoading(false)
      }
    },
    [setById, withRequest]
  )

  return {
    getAll,
    save,
    update,
    deleteSensorFactor,
    isLoading,
    isError,
  }
}
